import type { ReactNode } from 'react';
import { Checkbox, FormControlLabel } from '@mui/material';
import { useFormContext } from 'react-hook-form';

import { createDataTestId } from '@/utils/createDataTestId';

export type LeaseCheckboxInputProps = {
  name: string;
  label?: ReactNode;
  disabled?: boolean;
  onChangeValue?: (checked: boolean) => void;
};

// Boolean checkbox bound to RHF via `useFormContext`. Stores a boolean in form
// state (unlike the string-valued primitives).
export const LeaseCheckboxInput = ({
  name,
  label,
  disabled = false,
  onChangeValue,
}: LeaseCheckboxInputProps) => {
  const { watch, setValue, trigger } = useFormContext();
  const checked = Boolean(watch(name));
  const dataTestId = createDataTestId({ inputString: name, suffix: 'checkbox' });

  const handleChange = (_event: React.ChangeEvent<HTMLInputElement>, nextChecked: boolean) => {
    setValue(name, nextChecked, { shouldValidate: true, shouldDirty: true });
    trigger(name);
    onChangeValue?.(nextChecked);
  };

  return (
    <FormControlLabel
      control={
        <Checkbox
          checked={checked}
          onChange={handleChange}
          disabled={disabled}
          color="primary"
          data-testid={dataTestId}
        />
      }
      label={label ?? ''}
    />
  );
};
